/*
 * i18n-search.js
 * Custom predictive search for locales Shopify's /search/suggest rejects (ar, zh, ja, ko, th, vi).
 * Loaded only for those locales by snippets/i18n-search.liquid; every other locale never downloads it.
 * Data: templates/collection.search-index.liquid, search.search-index-articles.liquid,
 *       list-collections.search-index.liquid (rendered by Shopify in the active language).
 * Plan and rationale: md/custom-predictive-search/plan.md
 * Tests: node --test md/custom-predictive-search/tests/*.test.js
 */
(function () {
  'use strict';

  const INDEXES = {
    products: {
      path: 'collections/all?view=search-index',
      weights: { t: 4, k: 4, c: 2, y: 1, g: 1 }, // title, keywords, collections, type, tags
      max: 6,
    },
    articles: {
      path: 'search?q=*&type=article&view=search-index-articles',
      weights: { t: 4, g: 2 },
      max: 4,
    },
    collections: {
      path: 'collections?view=search-index',
      weights: { t: 4 },
      max: 4,
    },
  };
  const TTL_MS = 15 * 60 * 1000;
  const CACHE_VERSION = 'v1'; // bump when an index's fields change
  const DEBUG_KEY = 'i18n-search:debug';

  const AR = /[؀-ۿ]/;
  // Thai, Japanese kana, CJK ideographs, Hangul: matched by substring, never by segmented words,
  // because Intl.Segmenter splits e.g. 迪拜 into 迪 + 拜 and would match the characters anywhere.
  const NOSPACE = /[฀-๿぀-ヿ㐀-鿿豈-﫿가-힯]/;

  /* ---------- normalization ---------- */

  const FOLD = {
    'ـ': '', // tatweel
    'ء': '', // standalone hamza: صحراء = صحرا
    'ٱ': 'ا', // alef wasla
    'ة': 'ه', // taa marbuta
    'ى': 'ي', // alef maqsura
    'ی': 'ي', // Persian yeh
    'ک': 'ك', // Persian kaf
    'đ': 'd', // Vietnamese đ (not decomposed by NFKD)
  };
  const FOLD_RE = /[ـءٱةىیکđ٠-٩۰-۹]/g;
  const foldChar = (ch) => {
    if (ch in FOLD) return FOLD[ch];
    const c = ch.charCodeAt(0);
    return String(c - (c >= 0x06f0 ? 0x06f0 : 0x0660)); // Arabic-Indic / Persian digits
  };

  function norm(s) {
    const str = String(s == null ? '' : s);
    if (!/[^\x00-\x7F]/.test(str)) return str.toLowerCase(); // plain ASCII fast path
    return (
      str
        .normalize('NFKD') // splits accents and أ إ آ ؤ ئ; unfolds Arabic presentation forms and full-width Latin
        // Latin/Vietnamese accents + Arabic tashkeel, hamza/madda marks. Thai and Japanese marks are kept.
        .replace(/[̀-ًͯ-ٰٟۖ-ۭ]/g, '')
        .normalize('NFC') // recomposes Hangul syllables and kana split by NFKD
        .toLowerCase()
        .replace(FOLD_RE, foldChar)
    );
  }

  /* Light Arabic stemming: clitic prefixes + common suffixes. Applied identically to index and query. */
  function stemAr(t) {
    t = t.replace(/^(?:وال|بال|كال|فال|لل|ال)(?=..)/, '');
    if (t.length > 3) t = t.replace(/^و/, '');
    if (t.length > 4) t = t.replace(/(?:ات|ون|ين|يه)$/, '');
    if (t.length > 3) t = t.replace(/ه$/, '');
    return t;
  }

  /*
   * Splits already-normalized text into words. A plain regex is enough: scripts without spaces are
   * matched by substring on the full text, so Intl.Segmenter (much slower to build) is not needed.
   */
  function tokenize(text) {
    return text ? text.split(/[^\p{L}\p{M}\p{N}]+/u).filter(Boolean) : [];
  }

  function words(s) {
    return tokenize(norm(s));
  }

  function variants(w) {
    if (!AR.test(w)) return [w];
    const s = stemAr(w);
    return s && s !== w ? [w, s] : [w];
  }

  /* Each group is a list of alternatives (OR); every group must match (AND). */
  function queryGroups(q) {
    const groups = [];
    for (const chunk of norm(q).split(/\s+/)) {
      if (!chunk) continue;
      if (NOSPACE.test(chunk)) groups.push([chunk]);
      else for (const w of tokenize(chunk)) groups.push(variants(w));
    }
    return groups;
  }

  /* Pre-compiled matchers: substring for no-space scripts, word prefix otherwise. */
  function compile(groups) {
    return groups.map((group) =>
      group.map((v) => (NOSPACE.test(v) ? (f) => f.text.includes(v) : (f) => f.words.includes(' ' + v)))
    );
  }

  /* ---------- typo tolerance (only used when the exact pass finds nothing) ---------- */

  // Allowed typos by word length: none under 5 letters, 1 for 5-7, 2 for 8+.
  const typoBudget = (v) => (NOSPACE.test(v) ? 0 : v.length >= 8 ? 2 : v.length >= 5 ? 1 : 0);

  /*
   * Edit distance (insert, delete, substitute, swap adjacent) between `a` and the closest prefix of `b`,
   * so a half-typed misspelling still matches ("hellicop" → "helicopter"). Returns max + 1 once it exceeds max.
   */
  function prefixDistance(a, b, max) {
    const n = a.length;
    const m = Math.min(b.length, n + max);
    if (m < n - max) return max + 1;
    let prev2 = null;
    let prev = Array.from({ length: m + 1 }, (_, j) => j);
    for (let i = 1; i <= n; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= m; j++) {
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    let best = max + 1;
    for (let j = Math.max(0, n - max); j <= m; j++) if (prev[j] < best) best = prev[j];
    return best;
  }

  // Every distinct word in an index, computed once per index.
  const vocabCache = new WeakMap();
  function vocabOf(docs) {
    let vocab = vocabCache.get(docs);
    if (!vocab) {
      vocab = new Set();
      for (const d of docs) for (const k in d.f) for (const t of d.f[k].toks) vocab.add(t);
      vocabCache.set(docs, vocab);
    }
    return vocab;
  }

  /* Like compile(), but each long-enough word also accepts index words within its typo budget. */
  function compileFuzzy(groups, vocab) {
    return groups.map((group) =>
      group.map((v) => {
        const k = typoBudget(v);
        if (!k) return NOSPACE.test(v) ? (f) => f.text.includes(v) : (f) => f.words.includes(' ' + v);
        const near = new Set();
        for (const t of vocab) if (t.length >= v.length - k && prefixDistance(v, t, k) <= k) near.add(t);
        return (f) => f.toks.some((t) => near.has(t));
      })
    );
  }

  /* ---------- index + scoring ---------- */

  function buildDocs(items, weights) {
    const fields = Object.keys(weights);
    return items.map((item) => {
      const f = {};
      for (const k of fields) {
        const text = norm(item[k]);
        const toks = [];
        for (const w of tokenize(text)) toks.push(...variants(w));
        f[k] = { text, toks, words: ' ' + toks.join(' ') }; // ' ' + q found in words: some word starts with q
      }
      return { item, f };
    });
  }

  /* Exact pass first; only when it finds nothing, a second pass tolerates typos ("hellicoptor" → helicopter). */
  function searchIn(docs, q, weights, max) {
    const nq = norm(q).trim();
    if (!nq || (!NOSPACE.test(nq) && nq.length < 2)) return [];
    const groups = queryGroups(q);
    if (!groups.length) return [];
    const exact = rank(docs, nq, compile(groups), weights, max);
    if (exact.length || !groups.some((g) => g.some(typoBudget))) return exact;
    return rank(docs, nq, compileFuzzy(groups, vocabOf(docs)), weights, max);
  }

  function rank(docs, nq, groups, weights, max) {
    const fields = Object.keys(weights);
    const res = [];
    for (const d of docs) {
      let total = 0;
      for (const group of groups) {
        let best = 0;
        for (const k of fields) {
          const w = weights[k];
          if (w > best && group.some((m) => m(d.f[k]))) best = w;
        }
        if (!best) {
          total = 0;
          break;
        }
        total += best;
      }
      if (!total) continue;
      if (d.f.t && d.f.t.text.startsWith(nq)) total += 2; // title starts with the query
      res.push([total, d.item]);
    }
    res.sort((a, b) => b[0] - a[0] || String(a[1].t).length - String(b[1].t).length);
    return res.slice(0, max).map((r) => r[1]);
  }

  /* ---------- loading + caching ---------- */

  /*
   * One loader per page. load(kind) fetches page 1, then the remaining pages in parallel, and caches
   * the items in sessionStorage per kind + locale root. docs(kind) builds the search index once.
   * A failed load is forgotten so the next call retries.
   */
  function createLoader({ root, fetchImpl, storage, now = Date.now, log = () => {} }) {
    const loads = {};
    const built = {};
    const key = (kind) => `i18n-search:${kind}:${root}:${CACHE_VERSION}`;

    function readCache(kind) {
      try {
        const c = JSON.parse(storage.getItem(key(kind)) || 'null');
        if (c && now() - c.at < TTL_MS && Array.isArray(c.items)) return c.items;
      } catch (e) {}
      return null;
    }

    function writeCache(kind, items) {
      try {
        storage.setItem(key(kind), JSON.stringify({ at: now(), items }));
      } catch (e) {
        log(kind, 'cache write failed, using memory only', e && e.name);
      }
    }

    function getPage(kind, n) {
      const url = `${root}${INDEXES[kind].path}&page=${n}`;
      return fetchImpl(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(`${kind} page ${n}: HTTP ${r.status}`);
          return r.text();
        })
        .then((text) => ({ chars: text.length, data: JSON.parse(text) }));
    }

    function fetchAll(kind) {
      const t0 = now();
      return getPage(kind, 1).then((first) => {
        const rest = [];
        for (let p = 2; p <= first.data.pages; p++) rest.push(getPage(kind, p));
        return Promise.all(rest).then((pages) => {
          const all = [first, ...pages];
          const items = all.flatMap((p) => p.data.items || []);
          log(kind, 'network', {
            pages: all.length,
            items: items.length,
            chars: all.reduce((n, p) => n + p.chars, 0),
            ms: now() - t0,
          });
          writeCache(kind, items);
          return items;
        });
      });
    }

    function load(kind) {
      if (!loads[kind]) {
        const cached = readCache(kind);
        if (cached) log(kind, 'cache', { items: cached.length });
        loads[kind] = (cached ? Promise.resolve(cached) : fetchAll(kind)).catch((err) => {
          delete loads[kind];
          log(kind, 'load failed', err && err.message);
          throw err;
        });
      }
      return loads[kind];
    }

    function docs(kind) {
      if (!built[kind]) {
        built[kind] = load(kind)
          .then((items) => {
            const t0 = now();
            const d = buildDocs(items, INDEXES[kind].weights);
            log(kind, 'index built', { docs: d.length, ms: now() - t0 });
            return d;
          })
          .catch((err) => {
            delete built[kind];
            throw err;
          });
      }
      return built[kind];
    }

    return { key, load, docs };
  }

  /* ?i18n-search-debug=1 turns logging on for the tab, =0 turns it off. Silent by default. */
  function debugEnabled(search, storage) {
    try {
      const flag = new URLSearchParams(search).get('i18n-search-debug');
      if (flag === '1') storage.setItem(DEBUG_KEY, '1');
      else if (flag === '0') storage.removeItem(DEBUG_KEY);
      return storage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  const core = {
    INDEXES,
    norm,
    stemAr,
    words,
    queryGroups,
    prefixDistance,
    buildDocs,
    searchIn,
    createLoader,
    debugEnabled,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = core;
    return;
  }

  /* ---------- storefront wiring ---------- */

  const configEl = document.getElementById('i18n-search-config');
  if (!configEl) return;
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const storage = (() => {
    try {
      return window.sessionStorage;
    } catch (e) {
      return { getItem: () => null, setItem() {}, removeItem() {} };
    }
  })();
  const dbg = debugEnabled(window.location.search, storage)
    ? (...args) => console.log('[i18n-search]', ...args)
    : () => {};

  const KINDS = Object.keys(INDEXES);
  // Locale prefix from the URL itself (/ar/…, /zh-cn/…). On the live store routes.root_url renders as "/" on every
  // locale's pages (seen 2026-09-28 on /ar/, /tr/, /ru/, /de/), which would load the English index. It is only the
  // fallback, for URLs without a prefix.
  const firstSegment = window.location.pathname.split('/')[1] || '';
  const ROOT =
    config.locale && firstSegment.toLowerCase() === String(config.locale).toLowerCase()
      ? `/${firstSegment}/`
      : (config.root || '/').replace(/\/?$/, '/');
  const SEARCH_URL = `${ROOT}search`;

  // Same reason: search forms are rendered with action="/search", so "Search for …" and Enter would open English
  // results. In the locales this script runs in, point them at this locale's search page.
  if (ROOT !== '/') {
    for (const form of document.querySelectorAll('form[role="search"]')) {
      if (new URL(form.getAttribute('action') || '/', window.location.href).pathname === '/search') {
        form.setAttribute('action', SEARCH_URL);
      }
    }
  }
  const S = config.strings || {};
  const loader = createLoader({ root: ROOT, fetchImpl: (url, opts) => fetch(url, opts), storage, log: dbg });

  let features = {};
  try {
    features = JSON.parse(document.getElementById('shopify-features').textContent);
  } catch (e) {}
  dbg('active', { locale: config.locale, forced: config.forced, root: ROOT, shopifyPredictiveSearch: features.predictiveSearch });

  /* Loaded indexes, by kind. Filled as each one arrives so results can render progressively. */
  const ready = {};
  const docsFor = (kind) => loader.docs(kind).then((d) => (ready[kind] = d));

  let warmed = false;
  function warm() {
    if (warmed) return;
    warmed = true;
    dbg('warm-up');
    for (const kind of KINDS) {
      docsFor(kind).catch(() => {
        warmed = false; // the loader forgot the failure; the next interaction retries
      });
    }
  }

  // Start downloading before the user types: hovering or tapping the header search icon, or focusing any search box.
  const WARM_TARGETS =
    'details-modal.header__search summary, predictive-search, blog-predictive-search, #mobileSearchInput';
  for (const type of ['pointerover', 'touchstart', 'focusin']) {
    document.addEventListener(
      type,
      (e) => {
        if (!warmed && e.target.closest && e.target.closest(WARM_TARGETS)) warm();
      },
      { capture: true, passive: true }
    );
  }

  function runSearch(term) {
    const t0 = performance.now();
    const out = {};
    for (const kind of KINDS) {
      out[kind] = ready[kind] ? searchIn(ready[kind], term, INDEXES[kind].weights, INDEXES[kind].max) : [];
    }
    dbg('query', JSON.stringify(term), {
      normalized: norm(term),
      groups: queryGroups(term),
      products: out.products.length,
      collections: out.collections.length,
      articles: out.articles.length,
      loaded: KINDS.filter((k) => ready[k]),
      ms: +(performance.now() - t0).toFixed(2),
    });
    return out;
  }

  /* ---------- dropdown markup: mirrors sections/predictive-search.liquid ---------- */

  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC[c]);
  const fill = (s, term) => String(s || '').split('[terms]').join(term);
  const localUrl = (u) => (ROOT !== '/' && u && u[0] === '/' && !u.startsWith(ROOT) ? ROOT + u.slice(1) : u);
  const href = (u) => esc(localUrl(u));
  const fmtDate = (iso) => {
    const [y, m, d] = String(iso || '').split('-');
    return y && m && d ? `${m}/${d}/${y}` : '';
  };

  const tpl = document.getElementById('i18n-search-tpl');
  const spinnerHtml = (tpl && tpl.content.querySelector('.predictive-search__loading-state')?.outerHTML) || '';
  const arrowHtml = (tpl && tpl.content.querySelector('[data-part="arrow"]')?.innerHTML) || '';

  // Plural forms rendered by Liquid for one sample count per CLDR category; the sample number is swapped for the real one.
  const PLURAL_SAMPLE = { zero: '0', one: '1', two: '2', few: '3', many: '11', other: '100' };
  const plural = typeof Intl !== 'undefined' && Intl.PluralRules ? new Intl.PluralRules(config.locale) : null;
  function resultsText(n) {
    const R = S.results || {};
    const sample = PLURAL_SAMPLE[plural ? plural.select(n) : n === 1 ? 'one' : 'other'];
    const s = R[sample] || R['100'] || '';
    return s.replace(new RegExp(`(^|\\D)${sample}(?!\\d)`), `$1${n}`);
  }

  const option = (id, cls, inner) =>
    `<li id="${id}" class="predictive-search__list-item${cls}" role="option" aria-selected="false">${inner}</li>`;

  function suggestionsBlock(list) {
    const items = list
      .map((c, i) =>
        option(
          `predictive-search-option-collection-${i + 1}`,
          '',
          `<a href="${href(c.u)}" class="predictive-search__item link link--text" tabindex="-1">` +
            '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
            `<p class="predictive-search__item-heading h5">${esc(c.t)}</p></div></a>`
        )
      )
      .join('');
    return (
      '<div><h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">' +
      `${esc(S.suggestions)}</h2>` +
      '<ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">' +
      `${items}</ul></div>`
    );
  }

  function articlesBlock(list, variant) {
    const items = list
      .map((a, i) => {
        const tag = String(a.g || '').split(',')[0].trim();
        return option(
          `predictive-search-option-article-${variant}-${i + 1}`,
          ' predictive-search__article-item',
          `<a href="${href(a.u)}" class="predictive-search__item predictive-search__item--article link link--text" tabindex="-1">` +
            (a.i
              ? `<img class="predictive-search__article-image" src="${esc(a.i)}" alt="" width="60" height="60" loading="lazy">`
              : '') +
            '<div class="predictive-search__article-content">' +
            `<p class="predictive-search__item-heading predictive-search__article-title h5">${esc(a.t)}</p>` +
            '<div class="predictive-search__article-meta">' +
            `<span class="predictive-search__article-date">${esc(fmtDate(a.d))}</span>` +
            (tag ? `<span class="predictive-search__article-tag">${esc(tag.toUpperCase())}</span>` : '') +
            '</div></div></a>'
        );
      })
      .join('');
    return (
      '<div class="predictive-search__articles-wrapper">' +
      `<h2 id="predictive-search-articles-${variant}" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(S.articles)}</h2>` +
      `<ul id="predictive-search-results-articles-list-${variant}" class="predictive-search__results-list predictive-search__articles-list list-unstyled" role="group" aria-labelledby="predictive-search-articles-${variant}">` +
      `${items}</ul></div>`
    );
  }

  function productsBlock(list) {
    const items = list
      .map((p, i) =>
        option(
          `predictive-search-option-product-${i + 1}`,
          '',
          `<a href="${href(p.u)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">` +
            (p.i ? `<img class="predictive-search__image" src="${esc(p.i)}" alt="" width="50" height="50">` : '') +
            '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
            `<p class="predictive-search__item-heading h5">${esc(p.t)}</p></div></a>`
        )
      )
      .join('');
    return (
      '<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">' +
      `${esc(S.products)}</h2>` +
      '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">' +
      `${items}</ul></div>`
    );
  }

  function noResultsBlock() {
    return (
      '<div class="predictive-search__no-results"><div class="predictive-search__no-results-img-container">' +
      `<img src="${esc(config.notFoundImg)}" alt="${esc(S.noResultsTitle)}" class="predictive-search__no-results-img"></div>` +
      `<p>${esc(S.noResultsTitle)}<br><span class="predictive-search__no-results-hint">${esc(S.noResultsHint)}</span></p></div>`
    );
  }

  function buildMarkup(term, r) {
    const nC = r.collections.length;
    const nA = r.articles.length;
    const nP = r.products.length;
    const firstColumn = nC + nA;
    const total = firstColumn + nP;
    const e = esc(term);

    let h = '<div id="predictive-search-results" role="listbox">';
    if (!total) h += noResultsBlock();
    if (total) {
      h +=
        '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper' +
        (nP ? '' : ' predictive-search__results-groups-wrapper--no-products') +
        (nC ? '' : ' predictive-search__results-groups-wrapper--no-suggestions') +
        '">';
    }
    if (firstColumn) {
      h += '<div class="predictive-search__result-group">';
      if (nC) h += suggestionsBlock(r.collections);
      if (nA) h += articlesBlock(r.articles, 'desktop');
      h += '</div>';
    }
    if (nP || nA) {
      h += '<div class="predictive-search__result-group">';
      if (nP) h += productsBlock(r.products);
      if (nA) h += articlesBlock(r.articles, 'mobile');
      h += '</div>';
    }
    if (total) h += '</div>';
    h += spinnerHtml;
    h +=
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      `<span data-predictive-search-search-for-text>${fill(esc(S.searchFor), e)}</span>${arrowHtml}</button></div>`;
    h += '</div>';
    h += `<span class="hidden" data-predictive-search-live-region-count-value>${
      total ? esc(resultsText(total)) : fill(esc(S.noResults), e)
    }</span>`;
    return h;
  }

  /* ---------- Dawn override: only where results come from changes ---------- */

  function getSearchResults(searchTerm) {
    if (this.i18nComposing) return; // mid-IME (pinyin / kana): wait for compositionend
    const queryKey = searchTerm.replace(' ', '-').toLowerCase(); // same key as Dawn
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    warm();

    const settle = (kinds) => Promise.allSettled(kinds.map(docsFor));
    const show = () => {
      if (this.searchTerm !== searchTerm) return false; // a newer query took over
      const markup = buildMarkup(searchTerm, runSearch(searchTerm));
      if (KINDS.every((k) => ready[k])) {
        this.allPredictiveSearchInstances.forEach((el) => {
          if (el.cachedResults) el.cachedResults[queryKey] = markup;
        });
      }
      this.renderSearchResults(markup);
      return true;
    };

    // Products and collections first; articles (the biggest index) are added when they arrive.
    settle(['products', 'collections'])
      .then(() => {
        if (!ready.articles && (ready.products || ready.collections)) show();
        return settle(['articles']);
      })
      .then(() => {
        if (!KINDS.some((k) => ready[k])) {
          dbg('no index could be loaded; closing');
          if (this.searchTerm === searchTerm) this.close();
          return;
        }
        show();
      });
  }

  customElements.whenDefined('predictive-search').then(() => {
    customElements.get('predictive-search').prototype.getSearchResults = getSearchResults;
    dbg('Dawn <predictive-search> now uses the local index');
  });

  /*
   * ---------- API for the theme's other search UIs ----------
   * Used by assets/blog-predictive-search.js and snippets/mobile-bottom-sheet-new.liquid, which call
   * /search/suggest directly. They switch to this only when it exists, i.e. only in unsupported locales.
   */
  window.TVI18nSearch = {
    // This locale's full search page, e.g. /ar/search (see ROOT above for why routes.search_url is not used).
    searchUrl: SEARCH_URL,
    // Same markup the Section Rendering API returns for sections/predictive-search.liquid.
    markup(q) {
      warm();
      return Promise.allSettled(KINDS.map(docsFor)).then(() => buildMarkup(q, runSearch(q)));
    },
    // Articles shaped like /search/suggest.json resources.results.articles.
    searchArticles(q, max = 10) {
      warm();
      return docsFor('articles').then((docs) =>
        searchIn(docs, q, INDEXES.articles.weights, max).map((a) => ({
          title: a.t,
          url: localUrl(a.u),
          featured_image: a.i ? { url: a.i } : null,
          published_at: a.d ? `${a.d}T00:00:00` : null, // local midnight, so the date never shifts by timezone
        }))
      );
    },
  };

  // IME input (Chinese pinyin, Japanese kana): don't search half-composed text; search once characters are chosen.
  document.addEventListener(
    'compositionstart',
    (e) => {
      const el = e.target.closest && e.target.closest('predictive-search');
      if (el) el.i18nComposing = true;
    },
    true
  );
  document.addEventListener(
    'compositionend',
    (e) => {
      const el = e.target.closest && e.target.closest('predictive-search');
      if (!el) return;
      el.i18nComposing = false;
      if (typeof el.onChange === 'function') el.onChange();
    },
    true
  );
})();
